import React from 'react';
import { inventoryService } from '../../../services/inventoryService';
import type { FinishedGoodsProductionListItem } from '../../../services/inventory/inventoryMappers';
import { useLanguage } from '../../../features/language/useLanguage';

export type FgProductionSortKey = 'code' | 'fg_name' | 'qty_produced' | 'status' | 'created_at';
export type FgProductionStatusFilter = 'all' | 'draft' | 'completed' | 'cancelled';
export type FgProductionSortConfig = { key: FgProductionSortKey; direction: 'asc' | 'desc' };

export const useFinishedGoodsProductions = ({ enabled }: { enabled: boolean }) => {
  const { t } = useLanguage();
  const [productions, setProductions] = React.useState<FinishedGoodsProductionListItem[]>([]);
  const [productionsLoading, setProductionsLoading] = React.useState(false);
  const [productionsError, setProductionsError] = React.useState<string | null>(null);
  const [searchFilter, setSearchFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<FgProductionStatusFilter>('all');
  const [createdFrom, setCreatedFrom] = React.useState('');
  const [createdTo, setCreatedTo] = React.useState('');
  const [sortConfig, setSortConfig] = React.useState<FgProductionSortConfig>({
    key: 'created_at',
    direction: 'desc',
  });
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const [cancelTargetId, setCancelTargetId] = React.useState<string | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setProductionsLoading(true);
    setProductionsError(null);
    try {
      const list = await inventoryService.listFinishedGoodsProductions({ status: 'all' });
      setProductions(list);
    } catch (err: unknown) {
      const errorResponse = err as { response?: { data?: { error?: string; message?: string } } };
      const message =
        errorResponse?.response?.data?.error ||
        errorResponse?.response?.data?.message ||
        t('workOrders.fgProductions.errorLoad');
      setProductionsError(message);
    } finally {
      setProductionsLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  const filteredSorted = React.useMemo(() => {
    const searchKeyword = searchFilter.trim().toLowerCase();
    const fromTimestamp = createdFrom ? new Date(`${createdFrom}T00:00:00`).getTime() : null;
    const toTimestamp = createdTo ? new Date(`${createdTo}T23:59:59.999`).getTime() : null;

    const filtered = productions.filter((production) => {
      if (statusFilter !== 'all' && production.status !== statusFilter) return false;

      if (searchKeyword) {
        const code = (production.code || '').toLowerCase();
        const name = (production.fg_name || '').toLowerCase();
        const itemCode = (production.fg_code || '').toLowerCase();
        const isMatch = code.includes(searchKeyword) || name.includes(searchKeyword) || itemCode.includes(searchKeyword);
        if (!isMatch) return false;
      }

      if (fromTimestamp !== null || toTimestamp !== null) {
        const createdTimestamp = new Date(production.created_at).getTime();
        if (!Number.isFinite(createdTimestamp)) return false;
        if (fromTimestamp !== null && createdTimestamp < fromTimestamp) return false;
        if (toTimestamp !== null && createdTimestamp > toTimestamp) return false;
      }

      return true;
    });

    return [...filtered].sort((a, b) => {
      const directionMultiplier = sortConfig.direction === 'asc' ? 1 : -1;

      if (sortConfig.key === 'qty_produced') {
        return ((a.qty_produced || 0) - (b.qty_produced || 0)) * directionMultiplier;
      }

      if (sortConfig.key === 'created_at') {
        const left = new Date(a.created_at).getTime();
        const right = new Date(b.created_at).getTime();
        return ((Number.isFinite(left) ? left : 0) - (Number.isFinite(right) ? right : 0)) * directionMultiplier;
      }

      if (sortConfig.key === 'code') {
        return (a.code || '').localeCompare(b.code || '', undefined, { sensitivity: 'base', numeric: true }) * directionMultiplier;
      }

      if (sortConfig.key === 'status') {
        return a.status.localeCompare(b.status, undefined, { sensitivity: 'base' }) * directionMultiplier;
      }

      return a.fg_name.localeCompare(b.fg_name, undefined, { sensitivity: 'base' }) * directionMultiplier;
    });
  }, [productions, searchFilter, statusFilter, createdFrom, createdTo, sortConfig]);

  const handleSort = React.useCallback((key: FgProductionSortKey) => {
    setSortConfig((prev) => {
      if (prev.key === key) return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      return { key, direction: 'asc' };
    });
  }, []);

  const requestCancel = React.useCallback((id: string) => {
    setProductionsError(null);
    setCancelTargetId(id);
    setCancelConfirmOpen(true);
  }, []);

  const closeCancel = React.useCallback(() => {
    if (cancelSubmitting) return;
    setCancelConfirmOpen(false);
    setCancelTargetId(null);
  }, [cancelSubmitting]);

  const submitCancel = React.useCallback(async () => {
    if (!cancelTargetId) {
      setCancelConfirmOpen(false);
      return;
    }
    setCancelSubmitting(true);
    setProductionsError(null);
    try {
      await inventoryService.cancelFinishedGoodsProductionDraft(cancelTargetId);
      await refresh();
      setCancelConfirmOpen(false);
      setCancelTargetId(null);
    } catch (err: unknown) {
      const errorResponse = err as { response?: { data?: { error?: string; message?: string } } };
      const message =
        errorResponse?.response?.data?.error ||
        errorResponse?.response?.data?.message ||
        t('workOrders.fgProductions.cancelModal.errorCancel');
      setProductionsError(message);
      setCancelConfirmOpen(false);
      setCancelTargetId(null);
    } finally {
      setCancelSubmitting(false);
    }
  }, [cancelTargetId, refresh, t]);

  const setError = React.useCallback((value: string | null) => {
    setProductionsError(value);
  }, []);

  return {
    productions: filteredSorted,
    productionsLoading,
    productionsError,
    setError,
    searchFilter,
    setSearchFilter,
    statusFilter,
    setStatusFilter,
    createdFrom,
    setCreatedFrom,
    createdTo,
    setCreatedTo,
    sortConfig,
    handleSort,
    refresh,
    cancelConfirmOpen,
    cancelSubmitting,
    requestCancel,
    closeCancel,
    submitCancel,
  };
};
